import {
  COURSE_FIELD_LABEL, COURSE_FIELDS, COURSE_LIMITS, COURSE_MODE_LABEL, COURSE_MODES, courseFieldOf, courseFull, courseRemaining, courseSignupOpen, CURRICULUM_TEMPLATE, DEFAULT_COURSE_GOALS,
  programLabel, type CenterProgram, type CourseField, type CourseInput, type CourseMode, type CourseRequestStatus,
} from '@selfon/domain/browser';
import { useMemo, useState } from 'react';
import { api, type CourseRequestRow, type CourseRow, type MyCourseRequest, type MyCourses } from '../api';
import { useAuth } from '../auth/AuthContext';
import { Alert, Chip, Dialog, Field, Loading, PageHeader, useLoad, ViewDialog } from '../components/ui';
import { dateOnly, won } from '../format';
import './programs.css';

/*
 * 센터 수업: 보호자는 공개 수업을 살펴보고 수강을 신청하며(관심 표시는 이 화면에서만 유지), 원장은 수업을 만들어 공개하고 신청을 수락·거절한다.
 * 보호자 화면에는 다른 아이의 이름·개인정보를 표시하지 않는다(현재 인원은 숫자만). 아이 이름이 붙은 신청은 원장 화면에만 보인다.
 */

type FieldFilter = CourseField | 'ALL';
const tagOf = (c: CourseRow) => `${COURSE_FIELD_LABEL[c.field]} · ${COURSE_MODE_LABEL[c.mode]}`;
const goalsOf = (c: CourseRow) => (c.goals?.length ? c.goals : DEFAULT_COURSE_GOALS[c.field]);
const feeText = (c: CourseRow) => (c.fee === undefined ? '선생님과 상의' : `회당 ${won(c.fee)}`);

const REQUEST_CHIP: Record<CourseRequestStatus, { tone: 'ok' | 'warn' | 'muted'; label: string }> = {
  PENDING: { tone: 'warn', label: '검토 중' }, ACCEPTED: { tone: 'ok', label: '수강 확정' }, DECLINED: { tone: 'muted', label: '거절됨' }, CANCELLED: { tone: 'muted', label: '취소함' }, ENDED: { tone: 'muted', label: '수강 종료' },
};
const isActive = (r?: MyCourseRequest) => r?.status === 'PENDING' || r?.status === 'ACCEPTED';

/** 보호자 화면: 자녀가 연결된 센터의 공개 수업과 내 신청. */
export function GuardianCourses() {
  const q = useLoad(() => api.myCourses(), []);
  return (
    <>
      {q.error && <Alert>{q.error}</Alert>}
      {q.loading && !q.data ? <Loading /> : <CourseBrowser courses={q.data?.courses ?? []} noCenter={q.data?.children.length === 0} signup={q.data ?? undefined} onSignupChanged={q.reload} />}
    </>
  );
}

/** preview 이면 원장의 미리보기(목록을 받아 그대로 보여 줌). signup 이 있으면 수강 신청을 할 수 있다. */
export function CourseBrowser({ courses, noCenter, preview, signup, onSignupChanged }: { courses: CourseRow[]; noCenter?: boolean; preview?: boolean; signup?: MyCourses; onSignupChanged?: () => void }) {
  const [query, setQuery] = useState('');
  const [field, setField] = useState<FieldFilter>('ALL');
  const [openOnly, setOpenOnly] = useState(false);
  const [likedOnly, setLikedOnly] = useState(false);
  const [liked, setLiked] = useState<Set<string>>(() => new Set());
  const [openId, setOpenId] = useState<string | null>(null);
  const [curriculum, setCurriculum] = useState<CourseRow | null>(null);
  const published = courses.filter((c) => c.published);
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const view = published.filter((c) => (field === 'ALL' || c.field === field) && (!openOnly || !courseFull(c)) && (!likedOnly || liked.has(c.id))
    && words.every((w) => `${c.name} ${c.teacher}`.toLowerCase().includes(w)));
  const toggleLike = (id: string) => setLiked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const open = published.find((c) => c.id === openId) ?? null;
  const H = preview ? 'h2' : 'h1';
  const [applying, setApplying] = useState<CourseRow | null>(null);
  const [applyChild, setApplyChild] = useState('');
  const [applyNote, setApplyNote] = useState('');
  const [cancelling, setCancelling] = useState<MyCourseRequest | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** 이 수업의 센터에 연결된 자녀와, 자녀별 가장 최근 신청(최신순으로 온다) */
  const kidsOf = (c: CourseRow) => (signup?.children ?? []).filter((k) => k.centerId === c.centerId)
    .map((k) => ({ ...k, request: signup?.requests.find((r) => r.courseId === c.id && r.childId === k.id) }));
  const applicable = (c: CourseRow) => (courseSignupOpen(c) ? kidsOf(c).filter((k) => !isActive(k.request)) : []);
  const startApply = (c: CourseRow) => { setApplyChild(applicable(c)[0]?.id ?? ''); setApplyNote(''); setNotice(null); setOpenId(null); setApplying(c); };
  const childName = (id?: string) => signup?.children.find((k) => k.id === id)?.name ?? '';
  const courseName = (id?: string) => courses.find((c) => c.id === id)?.name ?? '수업';

  return (
    <div className="courses">
      <div className="page-head">
        <div>
          <p className="page-eyebrow">아이의 다음 경험을 함께 찾아요</p>
          <H>센터 수업</H>
          <p className="muted">수업을 살펴보고, 우리 아이에게 맞는 참여 방법을 선생님과 이야기해 보세요.</p>
        </div>
      </div>

      {notice && <Alert kind="ok">{notice}</Alert>}
      {signup && signup.requests.some((r) => isActive(r)) && (
        <section className="course-requests" aria-labelledby="my-requests">
          <h2 id="my-requests">신청한 수업</h2>
          <ul className="plain">
            {signup.requests.filter((r) => isActive(r)).map((r) => (
              <li key={r.id}>
                <span><strong>{courseName(r.courseId)}</strong> <span className="muted small">{childName(r.childId)} · {dateOnly(r.createdAt)} 신청</span></span>
                <span className="course-request-side">
                  <Chip tone={REQUEST_CHIP[r.status].tone}>{REQUEST_CHIP[r.status].label}</Chip>
                  {r.status === 'PENDING' && <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setNotice(null); setCancelling(r); }}>신청 취소</button>}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="course-filters" role="search" aria-label="수업 찾기">
        <label className="course-search">
          <span className="sr-only">수업 검색</span>
          <input type="search" placeholder="수업명이나 선생님으로 찾기" value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
        <div className="course-chips" role="group" aria-label="분야">
          {(['ALL', ...COURSE_FIELDS] as FieldFilter[]).map((f) => (
            <button key={f} type="button" className="course-chip" aria-pressed={field === f} onClick={() => setField(f)}>{f === 'ALL' ? '전체' : COURSE_FIELD_LABEL[f]}</button>
          ))}
        </div>
        <div className="course-toggles">
          <label><input type="checkbox" checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} /> 모집 중만</label>
          <label><input type="checkbox" checked={likedOnly} onChange={(e) => setLikedOnly(e.target.checked)} /> 관심 수업</label>
          <span className="muted small" role="status">{view.length}개 수업</span>
        </div>
      </div>

      {view.length === 0 ? (
        <div className="course-empty">
          <strong>{noCenter ? '연결된 센터가 없어요' : '조건에 맞는 수업이 없어요'}</strong>
          <p className="muted">{noCenter ? '센터에서 받은 연결 코드로 자녀를 연결하면 센터 수업을 볼 수 있어요.' : '다른 분야를 고르거나 검색 조건을 바꿔 보세요.'}</p>
        </div>
      ) : (
        <ul className="course-grid">
          {view.map((c) => {
            const full = courseFull(c);
            return (
              <li key={c.id} className="course-card">
                <div className="course-card-top">
                  <span className="course-tag">{tagOf(c)}</span>
                  <button type="button" className="course-like" aria-pressed={liked.has(c.id)} aria-label={`${c.name} 관심 수업${liked.has(c.id) ? ' 해제' : '으로 표시'}`} onClick={() => toggleLike(c.id)}>
                    <svg viewBox="0 0 24 24" width={20} height={20} aria-hidden="true"><path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z" /></svg>
                  </button>
                </div>
                <h3>{c.name}</h3>
                {c.summary && <p className="course-summary">{c.summary}</p>}
                <dl className="course-meta">
                  <div><dt>담당</dt><dd>{c.teacher}</dd></div>
                  <div><dt>대상</dt><dd>{c.audience || '선생님과 상의'}</dd></div>
                  <div><dt>일정</dt><dd>{c.schedule || '선생님과 상의'}</dd></div>
                </dl>
                <div className="course-seat">
                  <div className="course-seat-row">
                    <span className={`chip ${full ? 'chip-muted' : 'chip-ok'}`}>{full ? '정원 마감' : '모집 중'}</span>
                    <span className="muted small">참여 {c.enrolled}명 / 정원 {c.capacity}명</span>
                  </div>
                  <progress max={c.capacity} value={c.enrolled} aria-label={`${c.name} 참여 인원`} />
                </div>
                {kidsOf(c).filter((k) => k.request).map((k) => <p key={k.id} className="course-kid small"><span>{k.name}</span><Chip tone={REQUEST_CHIP[k.request!.status].tone}>{REQUEST_CHIP[k.request!.status].label}</Chip></p>)}
                <button type="button" className="link course-more" onClick={() => setOpenId(c.id)}>수업 자세히 보기 →</button>
              </li>
            );
          })}
        </ul>
      )}
      <p className="muted small course-foot">관심 표시는 이 화면을 이용하는 동안만 유지됩니다. 다른 아이의 이름이나 개인정보는 표시하지 않습니다.</p>

      <ViewDialog title={open?.name ?? '수업'} open={Boolean(open)} onClose={() => setOpenId(null)} wide
        actions={open && <>
          <button type="button" className="btn btn-outline" aria-pressed={liked.has(open.id)} onClick={() => toggleLike(open.id)}>{liked.has(open.id) ? '♥ 관심 수업 해제' : '♡ 관심 수업으로 표시'}</button>
          <button type="button" className={`btn ${applicable(open).length ? 'btn-outline' : 'btn-primary'}`} onClick={() => setCurriculum(open)}>12회기 커리큘럼 ↗</button>
          {applicable(open).length > 0 && <button type="button" className="btn btn-primary" onClick={() => startApply(open)}>수강 신청</button>}
        </>}>
        {open && <CourseDetail c={open} />}
        {open && signup && (
          <section className="course-signup" aria-label="수강 신청 안내">
            {kidsOf(open).filter((k) => k.request).map((k) => (
              <p key={k.id} className="course-kid">
                <span>{k.name}</span><Chip tone={REQUEST_CHIP[k.request!.status].tone}>{REQUEST_CHIP[k.request!.status].label}</Chip>
                {k.request!.status === 'DECLINED' && k.request!.reason && <span className="muted small">{k.request!.reason}</span>}
              </p>
            ))}
            {!courseSignupOpen(open) && !kidsOf(open).some((k) => isActive(k.request)) && <p className="muted small">{courseFull(open) ? '정원이 마감되어 지금은 신청할 수 없어요.' : '지금은 앱에서 신청을 받지 않아요. 선생님께 문의해 주세요.'}</p>}
          </section>
        )}
      </ViewDialog>
      <CurriculumDialog course={curriculum} onClose={() => setCurriculum(null)} />

      <Dialog title={`${applying?.name ?? ''} 수강 신청`} open={Boolean(applying)} onClose={() => setApplying(null)} submitLabel="신청하기"
        onSubmit={async () => {
          await api.requestCourse(applyChild, { courseId: applying!.id, note: applyNote.trim() || undefined });
          setNotice(`${childName(applyChild)}의 '${applying!.name}' 신청을 보냈습니다. 센터가 확인하면 수강이 확정됩니다.`);
          onSignupChanged?.();
        }}>
        {applying && <p className="muted">{applying.schedule || '일정은 선생님과 상의'} · {feeText(applying)}</p>}
        {applying && applicable(applying).length > 1 && (
          <Field id="ca-child" label="신청할 아이">
            <select id="ca-child" value={applyChild} onChange={(e) => setApplyChild(e.target.value)}>{applicable(applying).map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}</select>
          </Field>
        )}
        <Field id="ca-note" label={`센터에 전할 말 (선택, ${COURSE_LIMITS.note}자까지)`} hint="건강·장애 정보 같은 민감한 내용은 적지 말아 주세요.">
          <textarea id="ca-note" rows={3} maxLength={COURSE_LIMITS.note} value={applyNote} onChange={(e) => setApplyNote(e.target.value)} aria-describedby="ca-note-hint" />
        </Field>
      </Dialog>

      <Dialog danger title="수강 신청 취소" open={Boolean(cancelling)} onClose={() => setCancelling(null)} submitLabel="신청 취소"
        onSubmit={async () => {
          await api.cancelCourseRequest(cancelling!.childId, cancelling!.id);
          setNotice(`'${courseName(cancelling!.courseId)}' 신청을 취소했습니다.`);
          onSignupChanged?.();
        }}>
        <p>{childName(cancelling?.childId)}의 '{courseName(cancelling?.courseId)}' 신청을 취소합니다. 필요하면 다시 신청할 수 있습니다.</p>
      </Dialog>
    </div>
  );
}

function CourseDetail({ c }: { c: CourseRow }) {
  return (
    <div className="stack">
      <p className="course-tag">{tagOf(c)}</p>
      {c.description && <p>{c.description}</p>}
      <dl className="course-detail">
        <div><dt>담당 선생님</dt><dd>{c.teacher}</dd></div>
        <div><dt>참여 대상</dt><dd>{c.audience || '선생님과 상의'}</dd></div>
        <div><dt>수업 시간</dt><dd>{c.schedule || '선생님과 상의'}</dd></div>
        <div><dt>수업료</dt><dd>{feeText(c)}</dd></div>
        <div><dt>준비물</dt><dd>{c.materials || '없음'}</dd></div>
        <div><dt>방식</dt><dd>{COURSE_MODE_LABEL[c.mode]} · 참여 {c.enrolled}명 / 정원 {c.capacity}명{courseFull(c) ? ' (정원 마감)' : ''}</dd></div>
      </dl>
      <section aria-labelledby={`goals-${c.id}`}>
        <h3 id={`goals-${c.id}`}>이런 경험을 함께해요</h3>
        <ul className="course-goals">{goalsOf(c).map((g) => <li key={g}>{g}</li>)}</ul>
      </section>
      <div className="course-note" role="note">
        <strong>참여 전에 확인해 주세요</strong>
        <ul>
          <li>대상 연령은 안내 기준이에요. 아이의 발달과 관심에 따라 달라질 수 있어요.</li>
          <li>일정·수업료·참여 가능 여부는 담당 선생님과 확인해 주세요.</li>
          <li>수강 신청은 센터가 확인해 수락하면 확정돼요.</li>
        </ul>
      </div>
    </div>
  );
}

export function CurriculumDialog({ course, onClose }: { course: CourseRow | null; onClose: () => void }) {
  return (
    <ViewDialog title="12회기 커리큘럼" open={Boolean(course)} onClose={onClose} wide>
      {course && <p className="muted">{course.name} · {COURSE_FIELD_LABEL[course.field]}</p>}
      <p className="course-draft" role="note">구성 예시 · 실제 확정된 수업 계획이 아닙니다. 담당 선생님이 검토할 초안입니다.</p>
      <div className="table-wrap curriculum-wrap" tabIndex={0} role="region" aria-label="커리큘럼 표 (가로로 스크롤)">
        <table className="table curriculum">
          <caption className="sr-only">12회기 커리큘럼 구성 예시</caption>
          <thead><tr><th scope="col">회기</th><th scope="col">주제</th><th scope="col">주요 활동</th><th scope="col">확인할 내용</th></tr></thead>
          <tbody>
            {CURRICULUM_TEMPLATE.map((r, i) => (
              <tr key={r.topic}><td className="num">{i + 1}회기</td><th scope="row">{r.topic}</th><td>{r.activity}</td><td>{r.check}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </ViewDialog>
  );
}

/* ---------------- 원장: 센터 수업 관리 ---------------- */

type StatusFilter = 'ALL' | 'PUBLISHED' | 'OPEN' | 'FULL' | 'DRAFT';
const STATUS_CHIPS: Array<[StatusFilter, string]> = [['ALL', '전체'], ['PUBLISHED', '공개'], ['OPEN', '모집 중'], ['FULL', '정원 마감']];
const EMPTY_FORM: CourseInput = { name: '', field: 'LANGUAGE', mode: 'GROUP', teacher: '', audience: '', schedule: '', summary: '', description: '', materials: '', capacity: 6, enrolled: 0, published: false, signupOpen: true };
/** 저장되어 있는 수업 → 입력 폼·저장 요청 값 */
const inputOf = (c: CourseRow): CourseInput => ({ name: c.name, field: c.field, mode: c.mode, teacher: c.teacher, audience: c.audience, schedule: c.schedule, summary: c.summary, description: c.description, materials: c.materials, capacity: c.capacity, enrolled: c.enrolled, published: c.published, signupOpen: c.signupOpen, fee: c.fee, programId: c.programId });

export function CenterCourses() {
  const { me } = useAuth();
  const centerId = me!.centerId!;
  const q = useLoad(() => Promise.all([api.courses(centerId), api.staff(centerId), api.courseRequests(centerId), api.programs(centerId)]), [centerId]);
  const courses = q.data?.[0].courses ?? [];
  const staff = (q.data?.[1].staff ?? []).filter((s) => s.active);
  const requests = q.data?.[2].requests ?? [];
  const programs = q.data?.[3].programs ?? [];
  const pending = requests.filter((r) => r.status === 'PENDING');
  const enrolledNow = requests.filter((r) => r.status === 'ACCEPTED');
  const decided = requests.filter((r) => r.status === 'DECLINED' || r.status === 'ENDED').slice(0, 20);
  const [ending, setEnding] = useState<CourseRequestRow | null>(null);
  const courseOf = (id: string) => courses.find((c) => c.id === id);
  const [declining, setDeclining] = useState<CourseRequestRow | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [query, setQuery] = useState('');
  const [preview, setPreview] = useState(false);
  const [editing, setEditing] = useState<CourseRow | 'new' | null>(null);
  const [form, setForm] = useState<CourseInput>(EMPTY_FORM);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 순서 바꾸기: 끌기를 시작하면(또는 "순서 바꾸기"를 누르면) 수정 모드가 되고, 확인을 눌러야 저장된다
  const [order, setOrder] = useState<string[] | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [savingOrder, setSavingOrder] = useState(false);

  const stats = useMemo(() => ({
    ALL: courses.length, PUBLISHED: courses.filter((c) => c.published).length,
    OPEN: courses.filter((c) => !courseFull(c)).length, DRAFT: courses.filter((c) => !c.published).length,
  }), [courses]);
  const match = (c: CourseRow) => status === 'ALL' || (status === 'PUBLISHED' && c.published) || (status === 'DRAFT' && !c.published)
    || (status === 'OPEN' && !courseFull(c)) || (status === 'FULL' && courseFull(c));
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const filtered = courses.filter((c) => match(c) && words.every((w) => `${c.name} ${c.teacher}`.toLowerCase().includes(w)));
  // 순서는 거르지 않은 전체 목록에서만 바꾼다(일부만 보일 때 옮기면 결과를 짐작하기 어렵다)
  const canReorder = status === 'ALL' && words.length === 0 && courses.length > 1;
  const view = order ? order.map((id) => courseOf(id)).filter((c): c is CourseRow => Boolean(c)) : filtered;
  const startOrder = () => { setNotice(null); setError(null); setOrder(courses.map((c) => c.id)); };
  const moveTo = (id: string, target: number) => setOrder((o) => {
    if (!o) return o;
    const next = o.filter((x) => x !== id);
    next.splice(Math.max(0, Math.min(next.length, target)), 0, id);
    return next.join() === o.join() ? o : next;
  });
  const saveOrder = async () => {
    if (!order) return;
    if (order.join() === courses.map((c) => c.id).join()) { setOrder(null); return; }
    setSavingOrder(true); setError(null);
    try {
      await api.reorderCourses(centerId, order);
      setNotice('수업 순서를 저장했습니다. 보호자 화면에도 이 순서로 보입니다.');
      setOrder(null);
      q.reload();
    } catch (e) { setError(e instanceof Error ? e.message : '순서를 저장하지 못했습니다.'); }
    finally { setSavingOrder(false); }
  };

  const startEdit = (c: CourseRow | 'new') => {
    setForm(c === 'new' ? { ...EMPTY_FORM, teacher: staff[0] ? `${staff[0].name} 선생님` : '' }
      : inputOf(c));
    setEditing(c);
  };
  const togglePublish = async (c: CourseRow) => {
    setError(null); setNotice(null);
    try {
      const { course } = await api.updateCourse(centerId, c.id, { ...inputOf(c), published: !c.published, expectedVersion: c.version });
      setNotice(course.published ? `'${course.name}'을(를) 보호자 화면에 공개했습니다.` : `'${course.name}'을(를) 준비 중으로 바꿨습니다. 보호자 화면에서 보이지 않습니다.`);
      q.reload();
    } catch (e) { setError(e instanceof Error ? e.message : '처리하지 못했습니다.'); }
  };
  const accept = async (r: CourseRequestRow) => {
    setError(null); setNotice(null);
    try {
      await api.decideCourseRequest(centerId, r.id, { accept: true, expectedVersion: r.version });
      setNotice(`${r.clientName}의 '${courseOf(r.courseId)?.name ?? '수업'}' 신청을 수락했습니다. 현재 인원이 1명 늘었습니다.`);
      q.reload();
    } catch (e) { setError(e instanceof Error ? e.message : '처리하지 못했습니다.'); }
  };
  const statBtn = (k: 'ALL' | 'PUBLISHED' | 'OPEN' | 'DRAFT', label: string) => (
    <button type="button" className="course-stat" aria-pressed={status === k} onClick={() => setStatus(k)}><span>{label}</span><strong>{stats[k]}</strong></button>
  );
  const set = <K extends keyof CourseInput>(k: K, v: CourseInput[K]) => setForm((f) => ({ ...f, [k]: v }));
  // 프로그램을 고르면 그 프로그램에 등록된 선생님만 담당으로 고른다(프로그램 관리에서 정함)
  const program = programs.find((x) => x.id === form.programId);
  const teacherName = (id: string) => staff.find((x) => x.userId === id)?.name;
  const pool = program ? program.teacherIds.map(teacherName).filter((n): n is string => Boolean(n)) : staff.map((x) => x.name);
  const teacherOpts = [...new Set([...pool.map((n) => `${n} 선생님`), ...(form.teacher ? [form.teacher] : [])])];
  const pickProgram = (id: string) => {
    const pr = programs.find((x) => x.id === id) as CenterProgram | undefined;
    if (!pr) { setForm((f) => ({ ...f, programId: undefined })); return; }
    const names = pr.teacherIds.map(teacherName).filter((n): n is string => Boolean(n)).map((n) => `${n} 선생님`);
    setForm((f) => ({
      ...f, programId: pr.id,
      teacher: names.includes(f.teacher) ? f.teacher : names[0] ?? '',
      field: courseFieldOf(pr.area) ?? f.field,
      mode: pr.mode === 'GROUP' ? 'GROUP' : 'INDIVIDUAL',
      fee: f.fee ?? pr.basePrice,
      name: f.name || pr.name,
    }));
  };

  return (
    <div className="stack center-courses">
      <PageHeader eyebrow="CENTER PROGRAMS" title="센터 수업 관리" sub="센터가 준비한 경험을 보호자에게 알리는 공간입니다."
        actions={<>
          <button type="button" className="btn btn-outline" aria-pressed={preview} onClick={() => setPreview((v) => !v)}>{preview ? '관리 화면으로' : '보호자 화면 미리보기'}</button>
          <button type="button" className="btn btn-primary" onClick={() => startEdit('new')}>+ 수업 만들기</button>
        </>} />
      {notice && <Alert kind="ok">{notice}</Alert>}
      {(error || q.error) && <Alert>{error ?? q.error}</Alert>}
      {q.loading && !q.data && <Loading />}

      {preview ? (
        <section className="course-preview" aria-label="보호자 화면 미리보기">
          <p className="course-preview-label" role="note">보호자 화면 미리보기 · 공개 수업만 보입니다</p>
          <CourseBrowser courses={courses} preview />
        </section>
      ) : q.data && <>
        {pending.length > 0 && (
          <section className="course-requests" aria-labelledby="pending-requests">
            <h2 id="pending-requests">수강 신청 {pending.length}건</h2>
            <ul className="plain">
              {pending.map((r) => {
                const c = courseOf(r.courseId);
                const full = !c || courseFull(c);
                return (
                  <li key={r.id}>
                    <span>
                      <strong>{r.clientName}</strong> <span className="muted small">{c?.name ?? '수업'} · {dateOnly(r.createdAt)} 신청</span>
                      {r.note && <span className="course-request-note small">{r.note}</span>}
                    </span>
                    <span className="course-request-side">
                      {full && <span className="muted small">정원 마감</span>}
                      <button type="button" className="btn btn-primary btn-sm" disabled={full} onClick={() => accept(r)}>수락</button>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setDeclineReason(''); setDeclining(r); }}>거절</button>
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
        <div className="course-stats" role="group" aria-label="수업 현황 (누르면 거르기)">
          {statBtn('ALL', '전체 수업')}{statBtn('PUBLISHED', '보호자 공개')}{statBtn('OPEN', '모집 중')}{statBtn('DRAFT', '준비 중')}
        </div>
        <div className="course-filters">
          <div className="course-chips" role="group" aria-label="상태">
            {STATUS_CHIPS.map(([k, l]) => <button key={k} type="button" className="course-chip" aria-pressed={status === k} disabled={Boolean(order)} onClick={() => setStatus(k)}>{l}</button>)}
          </div>
          <label className="course-search">
            <span className="sr-only">수업 검색</span>
            <input type="search" placeholder="수업명이나 선생님으로 찾기" value={query} disabled={Boolean(order)} onChange={(e) => setQuery(e.target.value)} />
          </label>
          <span className="muted small" role="status">{view.length}개 수업</span>
          {!order && canReorder && <button type="button" className="btn btn-ghost btn-sm" onClick={startOrder} title="카드를 끌어서도 바꿀 수 있습니다">순서 바꾸기</button>}
        </div>
        {order && (
          <div className="course-reorder-bar" role="region" aria-label="순서 바꾸기">
            <p><strong>순서 바꾸는 중</strong> <span className="muted small">카드를 끌거나 ‹ › 로 옮긴 뒤 확인을 누르세요. 보호자 화면에도 이 순서로 보입니다.</span></p>
            <span className="course-reorder-actions">
              <button type="button" className="btn btn-ghost btn-sm" disabled={savingOrder} onClick={() => { setOrder(null); setDragId(null); }}>취소</button>
              <button type="button" className="btn btn-primary btn-sm" disabled={savingOrder} onClick={() => void saveOrder()}>{savingOrder ? '저장 중…' : '확인'}</button>
            </span>
          </div>
        )}
        {view.length === 0 ? (
          <div className="course-empty"><strong>{courses.length ? '조건에 맞는 수업이 없어요' : '아직 만든 수업이 없어요'}</strong>
            <p className="muted">{courses.length ? '다른 상태를 고르거나 검색 조건을 바꿔 보세요.' : '“+ 수업 만들기”로 첫 수업을 준비해 보세요. 만든 수업은 공개하기 전까지 보호자에게 보이지 않습니다.'}</p></div>
        ) : (
          <ul className="course-grid">
            {view.map((c, i) => {
              const full = courseFull(c);
              const draggable = Boolean(order) || canReorder;
              return (
                <li key={c.id} className={`course-card${order ? ' reordering' : ''}${dragId === c.id ? ' dragging' : ''}`} draggable={draggable}
                  onDragStart={(e) => {
                    if (!draggable) return;
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', c.id);
                    if (!order) startOrder();
                    setDragId(c.id);
                  }}
                  onDragOver={(e) => {
                    if (!dragId) return;
                    e.preventDefault();
                    if (dragId !== c.id) moveTo(dragId, i);
                  }}
                  onDrop={(e) => e.preventDefault()}
                  onDragEnd={() => setDragId(null)}>
                  <div className="course-card-top">
                    <span className="course-tag">{tagOf(c)}</span>
                    <span className={`chip ${c.published ? 'chip-info' : 'chip-muted'}`}>{c.published ? (c.signupOpen ? '공개 · 신청 받는 중' : '공개') : '준비 중'}</span>
                  </div>
                  <h3>{c.name}</h3>
                  <dl className="course-meta">
                    <div><dt>담당</dt><dd>{c.teacher}</dd></div>
                    <div><dt>대상</dt><dd>{c.audience || '-'}</dd></div>
                    <div><dt>일정</dt><dd>{c.schedule || '-'}</dd></div>
                    <div><dt>수업료</dt><dd>{c.fee === undefined ? '-' : won(c.fee)}</dd></div>
                  </dl>
                  <div className="course-seat">
                    <div className="course-seat-row">
                      <span className={full ? 'muted' : 'course-remaining'}>{full ? '정원 마감' : `잔여 ${courseRemaining(c)}자리`}</span>
                      <span className="muted small">{c.enrolled} / {c.capacity}명</span>
                    </div>
                    <progress max={c.capacity} value={c.enrolled} aria-label={`${c.name} 참여 인원`} />
                  </div>
                  {order ? (
                    <div className="course-actions">
                      <button type="button" className="btn btn-outline btn-sm" disabled={i === 0} aria-label={`${c.name} 앞으로`} onClick={() => moveTo(c.id, i - 1)}>‹ 앞으로</button>
                      <button type="button" className="btn btn-outline btn-sm" disabled={i === view.length - 1} aria-label={`${c.name} 뒤로`} onClick={() => moveTo(c.id, i + 1)}>뒤로 ›</button>
                      <span className="muted small">{i + 1}번째</span>
                    </div>
                  ) : (
                    <div className="course-actions">
                      <button type="button" className="btn btn-outline btn-sm" onClick={() => startEdit(c)}>수업 편집</button>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => togglePublish(c)}>{c.published ? '준비 중으로 전환' : '미리보기에 공개'}</button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {enrolledNow.length > 0 && (
          <section className="course-requests" aria-labelledby="enrolled-now">
            <h2 id="enrolled-now">수강 중 {enrolledNow.length}명</h2>
            <ul className="plain">
              {enrolledNow.map((r) => (
                <li key={r.id}>
                  <span><strong>{r.clientName}</strong> <span className="muted small">{courseOf(r.courseId)?.name ?? '수업'}</span></span>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEnding(r)}>수강 종료</button>
                </li>
              ))}
            </ul>
          </section>
        )}
        {decided.length > 0 && (
          <details className="course-decided">
            <summary>처리한 신청 {decided.length}건</summary>
            <ul className="plain">
              {decided.map((r) => (
                <li key={r.id}>
                  <span><strong>{r.clientName}</strong> <span className="muted small">{courseOf(r.courseId)?.name ?? '수업'}{r.reason ? ` · ${r.reason}` : ''}</span></span>
                  <Chip tone="muted">{r.status === 'ENDED' ? '수강 종료' : '거절'}</Chip>
                </li>
              ))}
            </ul>
          </details>
        )}
      </>}

      <Dialog danger title="수강 종료" open={Boolean(ending)} onClose={() => setEnding(null)} submitLabel="수강 종료"
        onSubmit={async () => {
          await api.endCourseRequest(centerId, ending!.id, ending!.version);
          setNotice(`${ending!.clientName}의 수강을 종료했습니다. 현재 인원이 1명 줄었습니다.`);
          q.reload();
        }}>
        <p>{ending?.clientName}의 '{ending ? courseOf(ending.courseId)?.name : ''}' 수강을 종료합니다. 보호자는 다시 신청할 수 있습니다.</p>
      </Dialog>

      <Dialog danger title="수강 신청 거절" open={Boolean(declining)} onClose={() => setDeclining(null)} submitLabel="거절"
        onSubmit={async () => {
          await api.decideCourseRequest(centerId, declining!.id, { accept: false, expectedVersion: declining!.version, reason: declineReason.trim() || undefined });
          setNotice(`${declining!.clientName}의 신청을 거절했습니다.`);
          q.reload();
        }}>
        <p>{declining?.clientName}의 '{declining ? courseOf(declining.courseId)?.name : ''}' 신청을 거절합니다.</p>
        <Field id="cd-reason" label={`사유 (선택, ${COURSE_LIMITS.reason}자까지)`} hint="보호자 화면에 그대로 보입니다.">
          <textarea id="cd-reason" rows={3} maxLength={COURSE_LIMITS.reason} value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} aria-describedby="cd-reason-hint" />
        </Field>
      </Dialog>

      <Dialog title={editing === 'new' ? '수업 만들기' : '수업 편집'} open={editing !== null} onClose={() => setEditing(null)} wide submitLabel="저장"
        onSubmit={async () => {
          const b = { ...form, capacity: Number(form.capacity), enrolled: Number(form.enrolled) };
          if (editing === 'new') {
            const { course } = await api.createCourse(centerId, b);
            setNotice(`'${course.name}'을(를) 준비 중으로 저장했습니다. 공개하면 보호자 화면에 보입니다.`);
          } else if (editing) {
            const { course } = await api.updateCourse(centerId, editing.id, { ...b, expectedVersion: editing.version });
            setNotice(`'${course.name}'을(를) 저장했습니다.`);
          }
          setError(null);
          q.reload();
        }}>
        <div className="course-form">
          <div className="course-form-wide">
            <Field id="cf-program" label="프로그램" hint={programs.length ? '먼저 고르면 담당 선생님·분야·방식·수업료가 프로그램에 맞춰집니다.' : '프로그램 관리에서 프로그램을 등록하면 여기서 고를 수 있습니다.'}>
              <select id="cf-program" value={form.programId ?? ''} onChange={(e) => pickProgram(e.target.value)}>
                <option value="">프로그램 없이 만들기</option>
                {programs.filter((x) => x.active || x.id === form.programId).map((x) => <option key={x.id} value={x.id}>{x.category} · {programLabel(x)}{x.active ? '' : ' (중단)'}</option>)}
              </select>
            </Field>
          </div>
          <Field id="cf-name" label={`수업 이름 (1~${COURSE_LIMITS.name}자)`}><input id="cf-name" required maxLength={COURSE_LIMITS.name} value={form.name} onChange={(e) => set('name', e.target.value)} /></Field>
          <Field id="cf-field" label="분야">
            <select id="cf-field" value={form.field} onChange={(e) => set('field', e.target.value as CourseField)}>{COURSE_FIELDS.map((f) => <option key={f} value={f}>{COURSE_FIELD_LABEL[f]}</option>)}</select>
          </Field>
          <Field id="cf-teacher" label="담당 선생님">
            <select id="cf-teacher" required value={form.teacher} onChange={(e) => set('teacher', e.target.value)}>
              <option value="" disabled>{program && !pool.length ? '이 프로그램에 등록된 선생님이 없습니다' : '선생님 선택'}</option>
              {teacherOpts.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field id="cf-mode" label="방식">
            <select id="cf-mode" value={form.mode} onChange={(e) => set('mode', e.target.value as CourseMode)}>{COURSE_MODES.map((m) => <option key={m} value={m}>{COURSE_MODE_LABEL[m]}</option>)}</select>
          </Field>
          <Field id="cf-aud" label="대상"><input id="cf-aud" maxLength={COURSE_LIMITS.audience} placeholder="예: 만 4~6세" value={form.audience} onChange={(e) => set('audience', e.target.value)} /></Field>
          <Field id="cf-sch" label="일정"><input id="cf-sch" maxLength={COURSE_LIMITS.schedule} placeholder="예: 매주 화 16:00–16:50" value={form.schedule} onChange={(e) => set('schedule', e.target.value)} /></Field>
          <div className="course-form-wide">
            <Field id="cf-sum" label="한 줄 소개"><input id="cf-sum" maxLength={COURSE_LIMITS.summary} value={form.summary} onChange={(e) => set('summary', e.target.value)} /></Field>
          </div>
          <div className="course-form-wide">
            <Field id="cf-desc" label={`수업 소개 (${form.description.length}/${COURSE_LIMITS.description}자)`}>
              <textarea id="cf-desc" rows={4} maxLength={COURSE_LIMITS.description} value={form.description} onChange={(e) => set('description', e.target.value)} />
            </Field>
          </div>
          <Field id="cf-mat" label="준비물"><input id="cf-mat" maxLength={COURSE_LIMITS.materials} value={form.materials} onChange={(e) => set('materials', e.target.value)} /></Field>
          <Field id="cf-cap" label="정원 (1~100, 현재 인원 이상)">
            <input id="cf-cap" type="number" inputMode="numeric" required min={Math.max(1, Number(form.enrolled) || 0)} max={COURSE_LIMITS.capacityMax} step={1} value={form.capacity} onChange={(e) => set('capacity', e.target.value as unknown as number)} />
          </Field>
          <Field id="cf-enr" label="현재 인원" hint="숫자만 저장합니다. 참여 아동 이름은 적지 않아요.">
            <input id="cf-enr" type="number" inputMode="numeric" required min={0} max={Number(form.capacity) || COURSE_LIMITS.capacityMax} step={1} value={form.enrolled} onChange={(e) => set('enrolled', e.target.value as unknown as number)} />
          </Field>
          <Field id="cf-fee" label="회당 수업료(원)" hint="비워 두면 보호자에게 ‘선생님과 상의’로 보입니다.">
            <input id="cf-fee" type="number" inputMode="numeric" min={0} max={COURSE_LIMITS.feeMax} step={1000} value={form.fee ?? ''} onChange={(e) => set('fee', e.target.value === '' ? undefined : Number(e.target.value))} aria-describedby="cf-fee-hint" />
          </Field>
          <Field id="cf-signup" label="보호자 수강 신청" hint="공개된 수업에 자리가 있을 때만 신청할 수 있습니다.">
            <select id="cf-signup" value={form.signupOpen ? 'Y' : 'N'} onChange={(e) => set('signupOpen', e.target.value === 'Y')} aria-describedby="cf-signup-hint">
              <option value="Y">신청 받기</option><option value="N">받지 않기</option>
            </select>
          </Field>
          <Field id="cf-pub" label="공개 상태" hint={editing === 'new' ? '새 수업은 준비 중으로 저장됩니다. 저장 후 카드에서 공개할 수 있어요.' : undefined}>
            <select id="cf-pub" value={form.published ? 'Y' : 'N'} disabled={editing === 'new'} onChange={(e) => set('published', e.target.value === 'Y')}>
              <option value="N">준비 중</option><option value="Y">보호자 공개</option>
            </select>
          </Field>
        </div>
      </Dialog>
    </div>
  );
}
